import React from 'react';
import { Box, Typography, Grid, TextField, InputLabel } from '@mui/material';
import { Field } from 'formik';
import { useSelector } from 'react-redux';
import { customStylesSelector } from 'store/selectors/selectors';
import { useT } from 'translation';

interface RenewalFormProps {
  errors: any;
  touched: any;
  fieldPrefix?: string; // allows for different field name prefixes in different forms
}

const RenewalForm: React.FC<RenewalFormProps> = ({ 
  errors, 
  touched,
  fieldPrefix = 'additional_renewal_info'
}) => {
  const { t } = useT();
  const customStyles = useSelector(customStylesSelector);

  const InputLabelStyle = {
    color: customStyles.input.color,
    fontSize: "14px",
    fontWeight: "500",
    mb: "10px",
    fontFamily: customStyles.muiTheme.typography.fontFamily,
  };

  const TextFieldStyle = {
    ".MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${customStyles?.input?.borderColor ?? "#a8a8a8"}`,
      "&:hover": {
        border: `1px solid ${customStyles?.input?.hover?.borderColor}`,
      },
    },
    ".MuiInputBase-root": {
      height: "54px",
      borderRadius: "6px",
      fontWeight: "normal",
      color: customStyles.input.color,
      backgroundColor: customStyles.input.backgroundColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${customStyles?.input?.active?.borderColor}`,
    },
  };

  return (
    <Box sx={{ marginBottom: "1.5rem" }}>
      <Typography variant="h6" gutterBottom>
        {t("Informações de Renovação")}
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InputLabel sx={InputLabelStyle}>
            {t("Qual é o código de identificação (C.I) da apólice anterior?")}
          </InputLabel>
          <Field
            as={TextField}
            fullWidth
            name={`${fieldPrefix}.ci_code`}
            sx={TextFieldStyle}
            error={
              touched?.[fieldPrefix]?.ci_code && 
              Boolean(errors?.[fieldPrefix]?.ci_code)
            }
            helperText={
              touched?.[fieldPrefix]?.ci_code && 
              errors?.[fieldPrefix]?.ci_code
            }
          />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: '#7aa7e1',
              fontSize: "12px"
            }}
          >
            {t("C.I. é o número que identifica sua apólice de seguro antiga. Ele ajuda a encontrar seu histórico quando você renova ou muda de seguradora. Caso não encontre número de C.I na apólice anterior, entre em contato com o seu corretor.")}
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <InputLabel sx={InputLabelStyle}>
            {t("Qual é a data de fim de vigência da apólice anterior?")}
          </InputLabel>
          <Field
            as={TextField}
            fullWidth
            name={`${fieldPrefix}.end_date`}
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            sx={TextFieldStyle}
            error={
              touched?.[fieldPrefix]?.end_date && 
              Boolean(errors?.[fieldPrefix]?.end_date)
            }
            helperText={
              touched?.[fieldPrefix]?.end_date && 
              errors?.[fieldPrefix]?.end_date
            }
          />
        </Grid>
        
        <Grid item xs={12}>
          <InputLabel sx={InputLabelStyle}>
            {t("Qual é o número da apólice anterior")}
          </InputLabel>
          <Field
            as={TextField}
            fullWidth
            name={`${fieldPrefix}.policy_number`}
            sx={TextFieldStyle}
            error={
              touched?.[fieldPrefix]?.policy_number && 
              Boolean(errors?.[fieldPrefix]?.policy_number)
            }
            helperText={
              touched?.[fieldPrefix]?.policy_number && 
              errors?.[fieldPrefix]?.policy_number
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RenewalForm; 